import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { TEAM_HUES } from '../game/constants';
import type { SkinPatternKey } from '../skinUnlocks';
import { audio } from '../game/audio/AudioEngine';
import { useSpotlight } from '../components/ui/spotlight-card';

// Heavy network/SDK modules — only loaded when the user actually
// clicks PLAY or opens the room browser. Keeps the landing under
// 200 KB gzip first-paint instead of 245 KB.
const MatchmakingGate  = lazy(() => import('../components/MatchmakingGate').then((m) => ({ default: m.MatchmakingGate })));
const RoomBrowserModal = lazy(() => import('../components/RoomBrowserModal').then((m) => ({ default: m.RoomBrowserModal })));
// SparklesCore lazy-loads tsparticles (~42 KB gzip). The landing is
// fully usable without particles — they fade in once the engine
// finishes initializing, ~150 ms after first paint.
const SparklesCore     = lazy(() => import('../components/ui/sparkles').then((m) => ({ default: m.SparklesCore })));

/**
 * MinimalLanding — neon stadium launcher.
 *
 * Visual concept (per designer reference):
 *   - Floating portrait-orientation glass card with a thin neon outline
 *     glow (cyan → purple). Reads as "this is the launch panel".
 *   - Behind it: a cosmic stadium scene — perspective hexagonal floor,
 *     multi-color particle trails (cyan / magenta / green / purple),
 *     soft spotlight beams from the top of the frame.
 *   - The PLAY button is the dominant element: octagonal chamfered
 *     shape with a cyan→purple→green gradient, hexagonal inner texture,
 *     and concentric pulse rings beneath it.
 *   - Brand logo "CellSnake" — "Cell" in light slate, "Snake" in a
 *     cyan-blue-purple gradient.
 *
 * Hierarchy (loudest first):
 *   1. PLAY button     — gradient + glow + ring pulses
 *   2. CellSnake mark  — top of card
 *   3. Name field      — cyan-outlined input + reroll
 *   4. Browse rooms    — small underlined cyan link
 *   5. Settings gear   — top-right corner
 *
 * All copy in English. Random handle pre-filled. Enter triggers Play
 * from anywhere outside an input. Escape closes overlays.
 */

const NAME_FIRST = [
  'Nova', 'Pulse', 'Rift', 'Vortex', 'Ember', 'Glacé', 'Cipher',
  'Kinetic', 'Prism', 'Blade', 'Orbit', 'Storm', 'Quasar', 'Helix',
  'Axis', 'Frost', 'Glitch', 'Neon', 'Flux', 'Pyre', 'Sage',
  'Shade', 'Tide', 'Vex', 'Wisp', 'Zed', 'Echo', 'Cinder', 'Atlas',
  'Byte', 'Dusk', 'Edge', 'Fang', 'Halo', 'Iris', 'Jinx',
] as const;

const NAME_LAST = [
  'Viper', 'Shade', 'Lynx', 'Hawk', 'Wraith', 'Drift', 'Spark',
  'Surge', 'Cobra', 'Falcon', 'Storm', 'Specter', 'Mantis', 'Onyx',
  'Crimson', 'Aether', 'Cypher', 'Lance', 'Lotus', 'Mercer',
  'Nexus', 'Oracle', 'Phantom', 'Quill', 'Raven', 'Sable', 'Talon',
  'Umbra', 'Vector', 'Wren',
] as const;

const PATTERN_LABELS: Record<SkinPatternKey, string> = {
  plasma:    'Plasma',
  glitch:    'Glitch',
  geometric: 'Geometric',
  bio:       'Bio',
};

function randomName(): string {
  const a = NAME_FIRST[Math.floor(Math.random() * NAME_FIRST.length)]!;
  const b = NAME_LAST[Math.floor(Math.random()  * NAME_LAST.length)]!;
  return `${a} ${b}`;
}

// Octagonal chamfered shape — clipPath used by the PLAY button and the
// outer chrome frame so they stay in sync. Stretched 15 % horizontally
// so the silhouette reads as "horizontal button", not "stop sign".
const OCTAGON_CLIP = 'polygon(15% 0%, 85% 0%, 100% 30%, 100% 70%, 85% 100%, 15% 100%, 0% 70%, 0% 30%)';

// Display font stack — Inter Black + Manrope fallbacks. The mockup uses
// a notably chunkier sans than Space Grotesk; this stack gives the same
// "rounded weighty geometric" silhouette without adding a font fetch.
const DISPLAY_FONT = '"Inter", "Manrope", "Segoe UI Variable", system-ui, sans-serif';

export function MinimalLanding({ onPlay }: { onPlay: () => void }) {
  const playerName       = useGameStore((s) => s.playerName);
  const setPlayerName    = useGameStore((s) => s.setPlayerName);
  const chosenHue        = useGameStore((s) => s.chosenHue);
  const setChosenHue     = useGameStore((s) => s.setChosenHue);
  const chosenPattern    = useGameStore((s) => s.chosenPattern);
  const setChosenPattern = useGameStore((s) => s.setChosenPattern);
  const setGameMode      = useGameStore((s) => s.setGameMode);

  // Roll a fresh random handle on landing if the player has never
  // chosen one OR is still on the auto-generated "Player1234" stub
  // (the old default the store used before we ran a proper name pool).
  // Two-word handles like "Nova Viper" feel like a real character;
  // "Player4258" feels like a placeholder.
  //
  // Also treat single-word ALL-CAPS hash-style handles ("PULSE",
  // "NOVA", etc.) from earlier experiments as stubs — we now use
  // two-word First Last names instead. Detection: contains no space
  // AND is fully uppercase.
  useEffect(() => {
    const isStub =
      !playerName ||
      /^Player\d+$/i.test(playerName) ||
      (!playerName.includes(' ') && playerName === playerName.toUpperCase());
    if (isStub) setPlayerName(randomName());
  }, [playerName, setPlayerName]);

  const [showSettings, setShowSettings] = useState(false);
  const [showBrowser, setShowBrowser]   = useState(false);
  const [gateOpen, setGateOpen]         = useState(false);
  // Card hover state — when cursor enters the card, the spotlight
  // intensity bumps + the outline glow becomes brighter. Reverts on
  // pointer leave. Adds "this is interactive" affordance.
  const [cardHover, setCardHover]       = useState(false);

  // Cyan ("תכלת") cursor-following spotlight applied to the popup
  // card itself. The ref attaches to the card div, the vars are
  // spread into its inline style so the pseudo-element CSS below
  // can read --sp-x / --sp-y / --sp-hue / --sp-size / --sp-intensity.
  const spotlight = useSpotlight({ hue: 190, size: 380, intensity: cardHover ? 0.5 : 0.32 });

  // Respect `prefers-reduced-motion`. When the user's OS asks for
  // less motion, we cut: parallax tilt, button breathing, sheen
  // sweep, particle motion. Accessibility win + battery save on
  // low-power devices. Detected once on mount.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ── Mouse parallax — card responds to cursor with subtle 3D tilt.
  //    Springy so it feels organic, not "wired to the mouse." Effect
  //    is restrained (±4 degrees max) so the card never feels like
  //    it's about to fly off — it should read as a heavy floating
  //    object that gently leans toward where you're looking.
  //    Disabled entirely when prefers-reduced-motion is set.
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateYSpring = useSpring(useTransform(mouseX, [-1, 1], reducedMotion ? [0, 0] : [4, -4]), { stiffness: 80, damping: 18 });
  const rotateXSpring = useSpring(useTransform(mouseY, [-1, 1], reducedMotion ? [0, 0] : [-3, 3]), { stiffness: 80, damping: 18 });

  const startPlay = (): void => {
    audio.unlock();
    setGameMode('live');
    setGateOpen(true);
  };

  // Track mouse for parallax — skip on touch devices (matchMedia
  // hover:hover keeps the effect off where it doesn't make sense).
  useEffect(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const onMove = (e: MouseEvent): void => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;   // -1..1
      const y = (e.clientY / window.innerHeight) * 2 - 1;  // -1..1
      mouseX.set(x);
      mouseY.set(y);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mouseX, mouseY]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      const inField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA');
      if (inField) return;
      if (e.key === 'Enter' && !showSettings && !showBrowser && !gateOpen) {
        e.preventDefault();
        startPlay();
      }
      if (e.key === 'Escape') {
        setShowSettings(false);
        setShowBrowser(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSettings, showBrowser, gateOpen]);

  return (
    // Scrollable landing: the hero (game entry) fills the first
    // viewport-height, and real English content flows BELOW the fold.
    // This is the AdSense-critical change — the root URL now serves
    // substantial crawlable text on the same screen ads would run on,
    // instead of being a canvas-only page. All modals are
    // position:fixed so scrolling never strands them.
    <div className="relative w-full h-full overflow-y-auto overflow-x-hidden" style={{ background: '#03060c' }}>
      {/* Mobile-specific overrides — per designer brief, drop the
          3D tilt below 480 px and shrink the PLAY button height so
          the card fits a phone viewport without scrolling. */}
      <style>{`
        @media (max-width: 480px) {
          .tilt-card { transform: none !important; }
          .play-button-wrapper { height: 104px !important; }
        }

        /* ── Animated wordmark gradient ─────────────────────────
           Slow horizontal shimmer on the "Snake" half of the title.
           Background extends 220% so the visible window pans
           naturally. 9-second loop is "noticeable on dwell, not
           distracting on glance" per UI/UX research notes. */
        .nx-snake-shimmer {
          background-image: linear-gradient(
            105deg,
            #57e3ff 0%,
            #4b9bff 25%,
            #9a6bff 50%,
            #57e3ff 75%,
            #4b9bff 100%
          );
          animation: nx-snake-pan 9s linear infinite;
        }
        .nx-snake-static {
          background-image: linear-gradient(180deg, #57e3ff 0%, #4b9bff 45%, #9a6bff 100%);
        }
        @keyframes nx-snake-pan {
          0%   { background-position: 0% 50%; }
          100% { background-position: 220% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .nx-snake-shimmer { animation: none; }
        }

        /* ── Floating corner chips (Browse / More games / Daily) ──
           Promoted out of the main card so the hero is uncluttered
           and the corners feel "premium app" rather than "menu
           list inside a card". Each chip has a subtle hover lift +
           inner glow. The .nx-corner-chip base class handles the
           transitions; individual color is set inline. */
        .nx-corner-chip {
          transition:
            transform 0.22s cubic-bezier(0.2, 0.7, 0.3, 1),
            box-shadow 0.22s cubic-bezier(0.2, 0.7, 0.3, 1),
            border-color 0.18s ease;
        }
        .nx-corner-chip:hover { transform: translateY(-2px); }
        .nx-corner-chip:active { transform: translateY(0); }

        /* ── Cursor-tracking spotlight on the popup card ──
           Uses --sp-x / --sp-y written by the useSpotlight hook on
           pointermove. Two pseudo-elements:
             ::before — soft inner glow (touches the body of the card)
             ::after  — bright thin rim that lights up under the cursor
           Both stay clipped inside the card's rounded shape.        */
        [data-spotlight]::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: inherit;
          background: radial-gradient(
            var(--sp-size, 360px) circle at var(--sp-x, -200px) var(--sp-y, -200px),
            hsl(var(--sp-hue, 190) 100% 70% / var(--sp-intensity, 0.28)),
            hsl(var(--sp-hue, 190) 100% 60% / 0) 70%
          );
          transition: background 80ms linear;
          z-index: 1;
        }
        [data-spotlight]::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: inherit;
          padding: 1px;
          background: radial-gradient(
            calc(var(--sp-size, 360px) * 0.7) circle at var(--sp-x, -200px) var(--sp-y, -200px),
            hsl(var(--sp-hue, 190) 100% 78% / 0.95),
            hsl(var(--sp-hue, 190) 100% 60% / 0) 65%
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          filter: brightness(1.2);
          z-index: 2;
        }
        /* Make sure the card's own content sits above the spotlight
           pseudo-elements so the logo / inputs / PLAY stay legible. */
        [data-spotlight] > * { position: relative; z-index: 3; }

        /* Custom scrollbar inside the Settings modal — thin cyan
           thumb on transparent track so it reads as part of the
           panel chrome rather than the browser default. */
        .settings-scroll::-webkit-scrollbar { width: 8px; }
        .settings-scroll::-webkit-scrollbar-track { background: transparent; }
        .settings-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(94,216,255,0.45), rgba(94,216,255,0.20));
          border-radius: 4px;
        }
        .settings-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, rgba(94,216,255,0.65), rgba(94,216,255,0.35));
        }
        .settings-scroll { scrollbar-width: thin; scrollbar-color: rgba(94,216,255,0.45) transparent; }
      `}</style>

      {/* ══ HERO VIEWPORT — everything game-entry lives inside this
             100vh container so its absolute layers stay pinned to the
             first screen while the page scrolls to content below. ══ */}
      <div className="relative" style={{ height: '100vh', minHeight: 620 }}>

      {/* ── Layer 1: deep cosmic gradient ─────────────────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 70% 50% at 50% 0%,   rgba(140, 80, 220, 0.18) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 20% 60%,  rgba(34, 200, 255, 0.10) 0%, transparent 65%),
            radial-gradient(ellipse 60% 40% at 80% 60%,  rgba(80, 230, 130, 0.08) 0%, transparent 65%),
            radial-gradient(ellipse 80% 50% at 50% 100%, rgba(0, 0, 0, 0.55)      0%, transparent 70%)
          `,
        }}
      />

      {/* ── Layer 2: spotlight beams from the top ────────────────── */}
      <div
        className="absolute inset-x-0 top-0 h-1/2 pointer-events-none"
        style={{
          background: `
            conic-gradient(from 230deg at 30% -10%,  transparent 0deg, rgba(160, 90, 255, 0.10) 30deg, transparent 60deg),
            conic-gradient(from 250deg at 50% -10%,  transparent 0deg, rgba(50, 200, 255, 0.10) 25deg, transparent 55deg),
            conic-gradient(from 270deg at 70% -10%,  transparent 0deg, rgba(90, 230, 150, 0.10) 30deg, transparent 60deg)
          `,
          mixBlendMode: 'screen',
        }}
      />

      {/* ── Layer 3: hexagonal floor with perspective ────────────── */}
      <div
        className="absolute inset-x-[-25%] bottom-0 h-[60%] pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 50% 80% at 50% 100%, rgba(80,170,255,0.18) 0%, transparent 60%),
            url("data:image/svg+xml;utf8,${encodeURIComponent(
              `<svg xmlns='http://www.w3.org/2000/svg' width='64' height='56'>
                <polygon points='32,2 62,18 62,38 32,54 2,38 2,18' fill='none' stroke='rgba(120,200,255,0.22)' stroke-width='1'/>
              </svg>`
            )}")
          `,
          backgroundRepeat: 'no-repeat, repeat',
          backgroundSize: '100% 100%, 64px 56px',
          transform: 'perspective(720px) rotateX(62deg) translateY(8%) scale(1.35)',
          transformOrigin: '50% 0%',
          maskImage: 'linear-gradient(180deg, transparent 0%, black 30%, black 75%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, black 30%, black 75%, transparent 100%)',
        }}
      />

      {/* ── Layer 4: 4 stacked SparklesCore layers, multi-color.
                       Reduced density compared to before so the
                       card+PLAY remain the focal point. Suspense
                       null fallback — landing is fully usable
                       without particles, they fade in async. */}
      <Suspense fallback={null}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-85">
            <SparklesCore id="cs-spk-cyan"    background="transparent" minSize={0.5} maxSize={1.4} particleDensity={45} particleColor="#7be7ff" speed={1.0} className="w-full h-full" />
          </div>
          <div className="absolute inset-0 opacity-65">
            <SparklesCore id="cs-spk-magenta" background="transparent" minSize={0.5} maxSize={1.6} particleDensity={35} particleColor="#d36bff" speed={0.9} className="w-full h-full" />
          </div>
          <div className="absolute inset-0 opacity-70">
            <SparklesCore id="cs-spk-green"   background="transparent" minSize={0.4} maxSize={1.3} particleDensity={30} particleColor="#67e88c" speed={0.8} className="w-full h-full" />
          </div>
          <div className="absolute inset-0 opacity-55">
            <SparklesCore id="cs-spk-amber"   background="transparent" minSize={0.3} maxSize={1.0} particleDensity={18} particleColor="#ffd16b" speed={0.7} className="w-full h-full" />
          </div>
        </div>
      </Suspense>

      {/* ── Layer 5: stadium roof crowd lights ──────────────────── */}
      <StadiumRoofLights />

      {/* ── Floating corner chips (Browse + More games).
                         Top-LEFT: Browse rooms (cyan — inbound to the app).
                         Top-RIGHT: More games (magenta-cyan — outbound to
                         the operator's blog).  Both are out of the main
                         card flow so the hero composition stays clean. */}
      <FloatingCornerChips
        onBrowse={() => setShowBrowser(true)}
        reducedMotion={reducedMotion}
      />

      {/* ── Layer 6: the popup card ─────────────────────────────── */}
      <div
        className="absolute inset-0 flex items-center justify-center px-4 pointer-events-none"
        style={{ perspective: '900px' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 120, damping: 18 }}
          className="pointer-events-auto relative tilt-card"
          style={{
            width: 'min(440px, calc(100vw - 32px))',
            transformStyle: 'preserve-3d',
            transformOrigin: '50% 50%',
            rotateY: rotateYSpring,
            rotateX: rotateXSpring,
          }}
        >
          {/* Outer ambient haze (sits behind everything else) */}
          <div
            className="absolute -inset-[10px] rounded-[38px] pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(150,90,255,0.40) 0%, rgba(80,140,255,0.30) 50%, rgba(80,200,255,0.40) 100%)',
              filter: 'blur(22px)',
              opacity: 0.55,
            }}
          />
          {/* Crisp outer rim — single bright stroke instead of a busy
              conic gradient. Reads cleaner from a glance. */}
          <div
            className="absolute -inset-[1px] rounded-[33px] pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(120,180,255,0.85) 0%, rgba(150,120,255,0.75) 50%, rgba(80,150,255,0.85) 100%)',
              padding: '1.5px',
              WebkitMask:
                'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          />

          {/* The card — backdrop blur + saturate + 4-layer box-
              shadow exactly per the designer's spec sheet, PLUS a
              cyan cursor-tracking spotlight (data-spotlight) added
              on top via the useSpotlight hook. */}
          <motion.div
            ref={spotlight.ref}
            data-spotlight
            onPointerEnter={() => setCardHover(true)}
            onPointerLeave={() => setCardHover(false)}
            className="relative rounded-[32px] overflow-hidden transition-shadow duration-300"
            style={{
              background:
                'linear-gradient(180deg, rgba(17,24,39,0.72) 0%, rgba(5,7,13,0.88) 100%), radial-gradient(circle at 50% 0%, rgba(56,189,248,0.14), transparent 42%)',
              border: '1px solid rgba(180,220,255,0.28)',
              backdropFilter: 'blur(22px) saturate(145%)',
              WebkitBackdropFilter: 'blur(22px) saturate(145%)',
              boxShadow: cardHover
                ? `
                  0 0 0 1px rgba(125,211,252,0.20) inset,
                  0 28px 90px rgba(0,0,0,0.65),
                  0 0 70px rgba(94,216,255,0.30),
                  0 0 110px rgba(94,216,255,0.18)
                `
                : `
                  0 0 0 1px rgba(125,211,252,0.08) inset,
                  0 24px 80px rgba(0,0,0,0.62),
                  0 0 48px rgba(56,189,248,0.16),
                  0 0 72px rgba(168,85,247,0.12)
                `,
              ...spotlight.vars,
            }}
          >
            {/* Inner top highlight */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse 90% 50% at 50% 0%, rgba(140,180,255,0.12) 0%, transparent 60%)',
              }}
            />

            {/* Top-right gear. Inline `style` positioning instead of
                Tailwind's `top-5 right-5` so it can't be RTL-flipped
                by an ancestor's `dir="rtl"` (the analytics dashboard
                sets that on its `<html>` and the user reported the
                gear was appearing in the LEFT corner — a classic
                logical-property flip). z-index 30 keeps it above the
                spotlight pseudo-elements and the inner highlight. */}
            <button
              onClick={() => setShowSettings(true)}
              style={{ position: 'absolute', top: 16, right: 16, zIndex: 30 }}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/[0.06] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              title="Settings"
              aria-label="Open settings"
            >
              <GearIcon />
            </button>

            <div className="relative px-9 pt-12 pb-9">
              {/* ── CellSnake wordmark — vertical gradient on Snake ─
                                  Snake gets a SLOW (~9 s) horizontal-gradient
                                  shimmer cycle so the eye catches it as
                                  "alive". Reduced motion: shimmer disabled
                                  via the .nx-no-shimmer class — see global
                                  style below. */}
              <div className="text-center mb-3">
                <h1
                  className="font-extrabold leading-none tracking-tight inline-flex items-baseline"
                  style={{
                    fontFamily: DISPLAY_FONT,
                    fontWeight: 900,
                    letterSpacing: '-0.030em',
                    fontSize: 'clamp(48px, 7vw, 64px)',
                  }}
                >
                  <span
                    style={{
                      background: 'linear-gradient(180deg, #e8eef7 0%, #b6c4d8 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: '0 2px 16px rgba(180,200,230,0.25)',
                      filter: 'drop-shadow(0 2px 14px rgba(180,200,230,0.15))',
                    }}
                  >
                    Cell
                  </span>
                  <span
                    className={reducedMotion ? 'nx-snake-static' : 'nx-snake-shimmer'}
                    style={{
                      backgroundSize: '220% 100%',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 0 18px rgba(110,150,255,0.65)) drop-shadow(0 0 32px rgba(150,90,255,0.35))',
                    }}
                  >
                    Snake
                  </span>
                </h1>
              </div>

              {/* ── Tagline — one-line promise that sets stakes. ─── */}
              <div
                className="text-center mb-4 text-[12px] uppercase tracking-[0.42em] font-semibold"
                style={{
                  color: 'rgb(150,180,220)',
                  fontFamily: DISPLAY_FONT,
                  textShadow: '0 0 14px rgba(94,216,255,0.35)',
                }}
              >
                eat · grow · outlast
              </div>

              {/* ── Live social proof — feels much warmer than a wordmark
                                  alone. Hidden until the fetch resolves so we
                                  don't show "0 today" briefly on cold load. */}
              <SocialProof />

              {/* ── Name field ─────────────────────────────────── */}
              <NameField name={playerName} setName={setPlayerName} />

              {/* ── PLAY button ────────────────────────────────── */}
              <div className="relative mt-10 mb-7 flex items-center justify-center">
                <PulseRings />
                <PlayButton onClick={startPlay} />
              </div>

              {/* ── Or press Enter (plain text, no chip) ──────── */}
              <div
                className="text-center text-[15px] mb-6"
                style={{ fontFamily: DISPLAY_FONT, color: 'rgb(170,185,210)', fontWeight: 500 }}
              >
                or press Enter
              </div>

              {/* ── Daily challenge chip — surfaces today's goal so a
                                  returning player sees their unfinished
                                  business the moment the landing loads. */}
              <DailyChallengeChip />

              {/* Browse rooms + More games used to live here as inline
                                  CTAs but the operator's feedback was that
                                  the card felt cluttered and "wouldn't make
                                  me want to play". Both are now floating
                                  pills in the top corners of the viewport
                                  — out of the primary visual flow but still
                                  immediately discoverable. */}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── Content footer — links to the game's text pages.  Serves
                   two audiences at once: (1) real players looking for
                   How-to-Play / Tips / FAQ, (2) Google's AdSense
                   reviewer + search crawler, both of which need
                   text-rich pages linked from the root to recognize
                   the site as more than "just a canvas".  Positioned
                   below the main card, always visible without
                   scrolling on desktop; on mobile it wraps naturally. */}
      <ContentFooter />

      {/* Scroll-discovery cue — a gently bobbing chevron just above
          the ContentFooter chips. Without it, most visitors never
          realize the page scrolls to real content. Clicking it
          smooth-scrolls to the about section. Hidden on very short
          viewports where the chips already hug the card. */}
      {!reducedMotion && (
        <motion.button
          onClick={() => document.getElementById('about-the-game')?.scrollIntoView({ behavior: 'smooth' })}
          className="nx-scroll-cue absolute left-1/2 z-10 pointer-events-auto"
          style={{
            bottom: 108,
            transform: 'translateX(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(148, 200, 255, 0.55)',
          }}
          animate={{ y: [0, 6, 0], opacity: [0.55, 0.9, 0.55] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          aria-label="Scroll down to read about the game"
        >
          <svg width="26" height="14" viewBox="0 0 26 14" fill="none" aria-hidden>
            <path d="M2 2l11 9L24 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.button>
      )}

      </div>
      {/* ══ END HERO VIEWPORT ══ */}

      {/* ── Below-the-fold content — real English text on the root
             URL. What the AdSense reviewer needs to see, and genuinely
             useful for players deciding whether to press Play. */}
      <BelowFold />

      {/* SETTINGS modal */}
      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        playerName={playerName}
        setPlayerName={setPlayerName}
        chosenHue={chosenHue}
        setChosenHue={setChosenHue}
        chosenPattern={chosenPattern}
        setChosenPattern={setChosenPattern}
      />

      {/* ROOM BROWSER + MATCHMAKING GATE — both lazy. Suspense
          fallback is null because the gate has its own connecting
          spinner once it's mounted; an extra loader between the
          button click and the gate would just be visual noise. */}
      <Suspense fallback={null}>
        {showBrowser && (
          <RoomBrowserModal
            open={showBrowser}
            identity={{ name: playerName, hue: chosenHue, pattern: chosenPattern }}
            onJoined={() => { setShowBrowser(false); onPlay(); }}
            onDismiss={() => setShowBrowser(false)}
            onAutoPick={() => { setShowBrowser(false); startPlay(); }}
          />
        )}
        {gateOpen && (
          <MatchmakingGate
            open={gateOpen}
            identity={{ name: playerName, hue: chosenHue, pattern: chosenPattern }}
            onResolved={(mode) => {
              setGateOpen(false);
              if (mode === 'live' || mode === 'training' || mode === 'spectating') {
                onPlay();
              }
            }}
            onDismiss={() => setGateOpen(false)}
          />
        )}
      </Suspense>
    </div>
  );
}

/** Stadium roof crowd lights — row of small bright dots high up that
 *  read as distant illuminated seating. Deterministic placement so
 *  it's identical every render. */
function StadiumRoofLights() {
  const dots = Array.from({ length: 70 }, (_, i) => i);
  return (
    <div className="absolute inset-x-0 top-[5%] h-[18%] pointer-events-none overflow-hidden">
      {dots.map((i) => {
        const x = (i / dots.length) * 100;
        const yOff = Math.sin(i * 1.7) * 30 + Math.cos(i * 0.9) * 18;
        const y = 40 + yOff;
        const size = 1.6 + ((i * 13) % 17) / 10;
        const palette = ['#7be7ff', '#d36bff', '#67e88c', '#ffd16b', '#cbeaff'];
        const color = palette[i % palette.length]!;
        const tw = 0.55 + ((i * 31) % 45) / 100;
        return (
          <motion.span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: size,
              height: size,
              background: color,
              boxShadow: `0 0 ${size * 4}px ${color}`,
            }}
            animate={{ opacity: [tw, tw * 0.4, tw] }}
            transition={{ duration: 2 + (i % 5) * 0.4, repeat: Infinity, ease: 'easeInOut', delay: (i % 9) * 0.2 }}
          />
        );
      })}
    </div>
  );
}


/** The big PLAY button — octagonal chamfered shape with bright outer
 *  rim, embossed dark inner body with prominent hex pattern, and the
 *  "PLAY" label glowing white. Built to read as a physical lit object,
 *  not a flat CSS button. */
function PlayButton({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.025, y: -2 }}
      whileTap={{ scale: 0.985, y: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 24 }}
      className="relative focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-[26px] play-button-wrapper"
      style={{ width: '100%', height: 132 }}
      aria-label="Play"
    >
      {/* ── Layer 2: bright outer rim (static). CYAN family only.
           No outer blurred halo — the operator found it too loud.
           Drop-shadows toned down (12px / 22px) so the button has
           presence without screaming. ─────────────────────── */}
      <span
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'conic-gradient(from 215deg at 50% 50%, #5ed8ff 0deg, #87e2ff 45deg, #c7f0ff 90deg, #5ed8ff 135deg, #2da7e8 180deg, #4dd8c8 225deg, #5ed8ff 270deg, #87e2ff 315deg, #5ed8ff 360deg)',
          clipPath: OCTAGON_CLIP,
          filter: 'drop-shadow(0 0 12px rgba(94,216,255,0.55)) drop-shadow(0 0 22px rgba(135,226,255,0.30))',
        }}
        aria-hidden
      />

      {/* ── Layer 3: vibrant inner body. All cyan/azure tones —
           deep navy-cyan up to bright ice-cyan, with a bright
           white highlight at top-left. The hex pattern overlay
           below adds the lit-from-within vein detail. ───────── */}
      <span
        className="absolute pointer-events-none"
        style={{
          inset: 7,
          background: `
            radial-gradient(ellipse 70% 60% at 30% 25%, rgba(255,255,255,0.28) 0%, transparent 55%),
            linear-gradient(120deg,
              #1c5a8a 0%,
              #1f7eb4 18%,
              #2da7e8 38%,
              #4dc6f5 58%,
              #5ed8ff 78%,
              #1f7eb4 100%)
          `,
          clipPath: OCTAGON_CLIP,
        }}
        aria-hidden
      />

      {/* ── Layer 4: bright hexagonal pattern overlay ─────────── */}
      <span
        className="absolute pointer-events-none"
        style={{
          inset: 7,
          backgroundImage:
            'url("data:image/svg+xml;utf8,' +
            encodeURIComponent(
              `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='34'>
                <polygon points='20,1 39,10.5 39,23.5 20,33 1,23.5 1,10.5' fill='none' stroke='rgba(180,225,255,0.42)' stroke-width='1.1'/>
              </svg>`
            ) +
            '")',
          backgroundSize: '40px 34px',
          clipPath: OCTAGON_CLIP,
          mixBlendMode: 'screen',
        }}
        aria-hidden
      />

      {/* ── Layer 5: top specular sheen (gives plastic/metal feel) */}
      <span
        className="absolute pointer-events-none"
        style={{
          inset: 7,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 30%, transparent 50%)',
          clipPath: OCTAGON_CLIP,
        }}
        aria-hidden
      />

      {/* ── Layer 5b: MOVING specular sheen — a soft white diagonal
                       band slowly travels across the button surface,
                       6 s per cycle. This is the "alive" replacement
                       for the old breathing outer halo: subtle motion
                       that catches the eye without flashing. ── */}
      <motion.span
        className="absolute pointer-events-none"
        style={{
          inset: 7,
          background:
            'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.22) 45%, rgba(220,240,255,0.30) 50%, rgba(255,255,255,0.22) 55%, transparent 70%)',
          backgroundSize: '300% 100%',
          clipPath: OCTAGON_CLIP,
          mixBlendMode: 'overlay',
        }}
        animate={{ backgroundPosition: ['100% 0%', '-100% 0%'] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear', repeatDelay: 1.4 }}
        aria-hidden
      />

      {/* ── Layer 6: inner edge highlight (1-px crisp inner ring
                       + a deep bottom shadow for embossed depth). */}
      <span
        className="absolute pointer-events-none"
        style={{
          inset: 7,
          boxShadow:
            'inset 0 1.5px 0 rgba(255,255,255,0.28), inset 0 -1.5px 0 rgba(0,0,0,0.35), inset 0 -8px 18px rgba(0,0,0,0.55), inset 0 6px 18px rgba(255,255,255,0.06)',
          clipPath: OCTAGON_CLIP,
        }}
        aria-hidden
      />

      {/* ── Layer 7: the PLAY label (responsive clamp per brief).
                       Glow shifted to cyan to match the new palette. */}
      <span
        className="absolute inset-0 flex items-center justify-center font-black text-white"
        style={{
          fontFamily: DISPLAY_FONT,
          fontSize: 'clamp(56px, 7.5vw, 78px)',
          fontWeight: 900,
          letterSpacing: '0.04em',
          textShadow:
            '0 0 20px rgba(255,255,255,0.95), 0 0 40px rgba(135,226,255,0.90), 0 0 80px rgba(94,216,255,0.60), 0 2px 6px rgba(0,0,0,0.55)',
        }}
      >
        PLAY
      </span>
    </motion.button>
  );
}

/** Concentric ring pulses beneath the PLAY button — emanate outward
 *  on a 2.4 s loop. Three rings staggered by 0.8 s so a new one starts
 *  before the previous fully fades. Ring color matches the cyan
 *  palette of the button. */
function PulseRings() {
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center" aria-hidden>
      {[0, 0.8, 1.6].map((delay, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full border"
          style={{
            width: 280,
            height: 130,
            borderColor: 'rgba(135,226,255,0.55)',
            boxShadow: '0 0 12px rgba(94,216,255,0.35)',
          }}
          initial={{ scale: 1, opacity: 0 }}
          animate={{ scale: [1, 1.5], opacity: [0.7, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay }}
        />
      ))}
    </div>
  );
}

function NameField({ name, setName }: { name: string; setName: (n: string) => void }) {
  const [focused, setFocused] = useState(false);
  const [spinning, setSpinning] = useState(false);

  return (
    <div className="flex items-center justify-center gap-3">
      <div className="relative flex-1 max-w-[280px]">
        <motion.div
          className="absolute inset-0 rounded-xl pointer-events-none"
          animate={{
            boxShadow: focused
              ? '0 0 0 1.5px rgba(80,200,255,0.85), 0 0 24px rgba(80,200,255,0.45), inset 0 0 24px rgba(80,200,255,0.15)'
              : '0 0 0 1.5px rgba(80,200,255,0.42), 0 0 12px rgba(80,200,255,0.18), inset 0 0 18px rgba(80,200,255,0.10)',
          }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 18))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          spellCheck={false}
          maxLength={18}
          placeholder="Enter your name"
          className="relative w-full text-center text-[20px] font-semibold py-3 px-4 rounded-xl bg-transparent focus:outline-none"
          style={{
            fontFamily: DISPLAY_FONT,
            fontWeight: 600,
            color: '#e8eef7',
            background: 'rgba(8,14,30,0.65)',
            caretColor: '#4dd3ff',
            letterSpacing: '0.01em',
          }}
        />
      </div>
      <motion.button
        onClick={() => { setName(randomName()); setSpinning((v) => !v); }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.92 }}
        className="relative w-12 h-12 rounded-xl flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/50"
        style={{
          border: '1.5px solid rgba(80,200,255,0.42)',
          background: 'rgba(8,14,30,0.65)',
          boxShadow: '0 0 12px rgba(80,200,255,0.18), inset 0 0 14px rgba(80,200,255,0.08)',
          color: '#9fd8ff',
        }}
        title="Random name"
        aria-label="Generate new random name"
      >
        <motion.span
          className="inline-flex"
          animate={{ rotate: spinning ? 360 : 0 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <RefreshIcon />
        </motion.span>
      </motion.button>
    </div>
  );
}

function SettingsModal({
  open,
  onClose,
  playerName,
  setPlayerName,
  chosenHue,
  setChosenHue,
  chosenPattern,
  setChosenPattern,
}: {
  open: boolean;
  onClose: () => void;
  playerName: string;
  setPlayerName: (n: string) => void;
  chosenHue: number;
  setChosenHue: (h: number) => void;
  chosenPattern: SkinPatternKey;
  setChosenPattern: (p: SkinPatternKey) => void;
}) {
  // ── Audio / display / data settings pulled directly from store.
  //    Centralising them here keeps the consumer (MinimalLanding) lean
  //    and lets the modal also be opened later from in-game without
  //    re-wiring props through HUD.
  const theme           = useGameStore((s) => s.theme);
  const toggleTheme     = useGameStore((s) => s.toggleTheme);
  const muted           = useGameStore((s) => s.hud.muted);
  const toggleMuteFn    = useGameStore((s) => s.toggleMute);
  const masterVolume    = useGameStore((s) => s.masterVolume);
  const setMasterVolume = useGameStore((s) => s.setMasterVolume);
  const musicVolume     = useGameStore((s) => s.musicVolume);
  const setMusicVolume  = useGameStore((s) => s.setMusicVolume);
  const musicMuted      = useGameStore((s) => s.musicMuted);
  const toggleMusicMuted = useGameStore((s) => s.toggleMusicMuted);
  const fxIntensity     = useGameStore((s) => s.fxIntensity);
  const setFxIntensity  = useGameStore((s) => s.setFxIntensity);
  const resetProgress   = useGameStore((s) => s.resetProgress);

  const [confirmReset, setConfirmReset] = useState(false);
  // Reset the confirm flag whenever the modal closes so the next open
  // starts fresh (no stale "are you sure?" surfaced).
  useEffect(() => { if (!open) setConfirmReset(false); }, [open]);

  const accent = `hsl(${chosenHue} 100% 70%)`;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            initial={{ scale: 0.92, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            className="relative w-full max-w-[460px] rounded-[28px] overflow-hidden flex flex-col"
            style={{
              maxHeight: 'min(86vh, 760px)',
              background:
                'linear-gradient(180deg, rgba(10,16,28,0.96) 0%, rgba(6,10,20,0.98) 100%)',
              backdropFilter: 'blur(20px) saturate(140%)',
              border: '1px solid rgba(150,170,220,0.18)',
              boxShadow: `
                0 40px 100px -20px rgba(0,0,0,0.75),
                0 0 80px -10px rgba(120,150,255,0.35),
                0 0 0 1px rgba(255,255,255,0.04) inset
              `,
            }}
          >
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, boxShadow: `0 0 8px ${accent}aa` }}
            />

            {/* Header — stays sticky at top while content scrolls below. */}
            <div className="px-7 pt-7 pb-5 flex items-center justify-between flex-shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-[0.45em] font-bold text-slate-400">preferences</div>
                <div className="text-[20px] font-bold text-white mt-0.5" style={{ fontFamily: DISPLAY_FONT, fontWeight: 800 }}>
                  Settings
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                aria-label="Close"
              >
                <span className="text-lg" aria-hidden>✕</span>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="px-7 pb-7 overflow-y-auto flex-1 settings-scroll">
              {/* ───── Profile (Your Snake) ───── */}
              <SettingsSection title="Your Snake" subtitle="Profile">
                <div className="mb-4">
                  <SettingsLabel>Name</SettingsLabel>
                  <div className="flex gap-2">
                    <input
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value.slice(0, 18))}
                      maxLength={18}
                      spellCheck={false}
                      className="flex-1 px-4 py-2.5 rounded-xl text-white text-[15px] text-center font-semibold focus:outline-none"
                      style={{
                        fontFamily: DISPLAY_FONT,
                        fontWeight: 600,
                        border: '1.5px solid rgba(80,200,255,0.40)',
                        background: 'rgba(10,20,40,0.55)',
                        caretColor: '#4dd3ff',
                      }}
                    />
                    <button
                      onClick={() => setPlayerName(randomName())}
                      className="w-11 h-11 rounded-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                      style={{
                        border: '1.5px solid rgba(80,200,255,0.40)',
                        background: 'rgba(10,20,40,0.55)',
                        color: '#9fd8ff',
                      }}
                      title="Random name"
                    >
                      <RefreshIcon />
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <SettingsLabel>Color</SettingsLabel>
                  <div className="grid grid-cols-8 gap-2">
                    {TEAM_HUES.map((h) => {
                      const selected = chosenHue === h;
                      return (
                        <button
                          key={h}
                          onClick={() => setChosenHue(h)}
                          className="aspect-square rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                          style={{
                            background: `radial-gradient(circle at 30% 30%, hsl(${h} 100% 75%) 0%, hsl(${h} 95% 50%) 100%)`,
                            boxShadow: selected
                              ? `0 0 0 2px white, 0 0 18px hsl(${h} 100% 60% / 0.8), inset 0 -4px 10px rgba(0,0,0,0.25)`
                              : 'inset 0 -3px 8px rgba(0,0,0,0.25)',
                          }}
                          aria-label={`Hue ${h}`}
                        />
                      );
                    })}
                  </div>
                </div>

                <div>
                  <SettingsLabel>Pattern</SettingsLabel>
                  <div className="grid grid-cols-4 gap-2">
                    {(Object.keys(PATTERN_LABELS) as SkinPatternKey[]).map((p) => {
                      const selected = chosenPattern === p;
                      return (
                        <button
                          key={p}
                          onClick={() => setChosenPattern(p)}
                          className="px-2 py-3 rounded-xl text-[11px] uppercase tracking-[0.2em] font-bold transition-all"
                          style={{
                            fontFamily: DISPLAY_FONT,
                            background: selected
                              ? `linear-gradient(180deg, ${accent}28, ${accent}10)`
                              : 'rgba(255,255,255,0.03)',
                            border: selected
                              ? `1px solid ${accent}77`
                              : '1px solid rgba(255,255,255,0.08)',
                            color: selected ? 'white' : 'rgba(255,255,255,0.55)',
                            boxShadow: selected ? `0 0 16px ${accent}33 inset` : undefined,
                          }}
                        >
                          {PATTERN_LABELS[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </SettingsSection>

              {/* ───── Display ───── */}
              <SettingsSection title="Display" subtitle="Visuals">
                <div className="mb-4">
                  <SettingsLabel>Theme</SettingsLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { id: 'dark',  label: 'Dark',  icon: '☾' },
                      { id: 'light', label: 'Light', icon: '☀' },
                    ] as const).map((opt) => {
                      const selected = theme === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => { if (theme !== opt.id) toggleTheme(); }}
                          className="px-3 py-3 rounded-xl text-[12px] uppercase tracking-[0.18em] font-bold transition-all inline-flex items-center justify-center gap-2"
                          style={{
                            fontFamily: DISPLAY_FONT,
                            background: selected
                              ? 'linear-gradient(180deg, rgba(94,216,255,0.20), rgba(94,216,255,0.05))'
                              : 'rgba(255,255,255,0.03)',
                            border: selected
                              ? '1px solid rgba(94,216,255,0.55)'
                              : '1px solid rgba(255,255,255,0.08)',
                            color: selected ? 'white' : 'rgba(255,255,255,0.55)',
                            boxShadow: selected ? '0 0 16px rgba(94,216,255,0.20) inset' : undefined,
                          }}
                        >
                          <span className="text-base" aria-hidden>{opt.icon}</span>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <SettingsHint>Affects the landing UI. The arena always stays dark.</SettingsHint>
                </div>

                <SettingsSlider
                  label="FX Intensity"
                  value={fxIntensity}
                  onChange={setFxIntensity}
                  formatValue={(v) => `${Math.round(v * 100)}%`}
                  hint="Lower if your device feels sluggish."
                />
              </SettingsSection>

              {/* ───── Audio ───── */}
              <SettingsSection title="Audio" subtitle="Sound">
                <SettingsSlider
                  label="Master volume"
                  value={muted ? 0 : masterVolume}
                  onChange={(v) => {
                    if (muted && v > 0 && toggleMuteFn) toggleMuteFn();
                    setMasterVolume(v);
                  }}
                  formatValue={(v) => muted ? 'Muted' : `${Math.round(v * 100)}%`}
                  rightAction={
                    <SettingsToggle
                      on={!muted}
                      onChange={() => toggleMuteFn?.()}
                      label="On"
                    />
                  }
                />
                <SettingsSlider
                  label="Music"
                  value={musicMuted ? 0 : musicVolume}
                  onChange={(v) => {
                    if (musicMuted && v > 0) toggleMusicMuted();
                    setMusicVolume(v);
                  }}
                  formatValue={(v) => musicMuted ? 'Muted' : `${Math.round(v * 100)}%`}
                  rightAction={
                    <SettingsToggle
                      on={!musicMuted}
                      onChange={toggleMusicMuted}
                      label="On"
                    />
                  }
                />
              </SettingsSection>

              {/* ───── Data ───── */}
              <SettingsSection title="Data" subtitle="Reset">
                <SettingsLabel>Progress</SettingsLabel>
                <div className="text-[12px] text-slate-400 mb-3 leading-relaxed">
                  Clears match history, achievements, daily streak, and the welcome
                  flag. Your audio + theme preferences stay. This reloads the page.
                </div>
                {!confirmReset ? (
                  <button
                    onClick={() => setConfirmReset(true)}
                    className="w-full px-4 py-2.5 rounded-xl text-[12px] uppercase tracking-[0.2em] font-bold transition-all"
                    style={{
                      fontFamily: DISPLAY_FONT,
                      background: 'rgba(251,113,133,0.08)',
                      border: '1px solid rgba(251,113,133,0.35)',
                      color: 'rgb(254,205,211)',
                    }}
                  >
                    Reset progress
                  </button>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setConfirmReset(false)}
                      className="px-4 py-2.5 rounded-xl text-[12px] uppercase tracking-[0.2em] font-bold transition-all"
                      style={{
                        fontFamily: DISPLAY_FONT,
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.85)',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => resetProgress()}
                      className="px-4 py-2.5 rounded-xl text-[12px] uppercase tracking-[0.2em] font-bold transition-all"
                      style={{
                        fontFamily: DISPLAY_FONT,
                        background: 'linear-gradient(180deg, rgba(251,113,133,0.30), rgba(251,113,133,0.18))',
                        border: '1px solid rgba(251,113,133,0.65)',
                        color: 'white',
                        boxShadow: '0 0 18px rgba(251,113,133,0.25)',
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                )}
              </SettingsSection>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Settings primitives ────────────────────────────────────────────

function SettingsSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="pt-5 pb-6"
      style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div className="mb-4">
        <div className="text-[9px] uppercase tracking-[0.42em] font-bold text-slate-400">{subtitle}</div>
        <div className="text-[16px] font-bold text-white mt-0.5" style={{ fontFamily: DISPLAY_FONT, fontWeight: 700 }}>
          {title}
        </div>
      </div>
      {children}
    </section>
  );
}

function SettingsLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.38em] text-slate-400 mb-2">
      {children}
    </div>
  );
}

function SettingsHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
      {children}
    </div>
  );
}

function SettingsSlider({
  label,
  value,
  onChange,
  formatValue,
  rightAction,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  formatValue: (v: number) => string;
  rightAction?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <SettingsLabel>{label}</SettingsLabel>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-white/65 font-mono tabular-nums min-w-[40px] text-right">
            {formatValue(value)}
          </span>
          {rightAction}
        </div>
      </div>
      <div className="relative h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{
            width: `${Math.max(0, Math.min(1, value)) * 100}%`,
            background: 'linear-gradient(90deg, rgba(94,216,255,0.7), rgba(135,226,255,0.95))',
            boxShadow: '0 0 12px rgba(94,216,255,0.4)',
          }}
        />
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label={label}
        />
      </div>
      {hint && <SettingsHint>{hint}</SettingsHint>}
    </div>
  );
}

function SettingsToggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onChange}
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="relative w-10 h-5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/50"
      style={{
        background: on ? 'rgba(94,216,255,0.35)' : 'rgba(255,255,255,0.08)',
        border: `1px solid ${on ? 'rgba(94,216,255,0.65)' : 'rgba(255,255,255,0.15)'}`,
      }}
    >
      <motion.span
        className="absolute top-[1px] w-[15px] h-[15px] rounded-full"
        animate={{ left: on ? 22 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        style={{
          background: on ? '#ffffff' : 'rgba(255,255,255,0.55)',
          boxShadow: on ? '0 0 8px rgba(94,216,255,0.7)' : 'none',
        }}
      />
    </button>
  );
}

// ── Tiny inline SVG icons ──────────────────────────────────────────

function GearIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 1 1-3-6.7M21 5v5h-5"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}


/**
 * FloatingCornerChips — Browse rooms (top-left) + More games (top-
 * right). Lifts the secondary CTAs OUT of the main hero card so the
 * card composition stays focused on the wordmark + name + PLAY. The
 * chips themselves are small, beautifully detailed, and read as
 * "tools sitting at the corners of the app" instead of "stuff
 * crammed into the menu".
 *
 * Top-left = inbound (Browse — stays in the game).
 * Top-right = outbound (More games — opens the blog in a new tab).
 *
 * Each chip:
 *   - Icon tile + label + arrow
 *   - 2px hover lift + brightening rim glow
 *   - Sheen sweep on hover
 *   - Subtle breathing aura on the icon (3.4s loop)
 *   - Reduced motion disables breathing + sheen
 *
 * NOTE: the chips are absolute-positioned to the viewport, not the
 * hero card. They don't compete with the parallax tilt either.
 */
function FloatingCornerChips({
  onBrowse,
  reducedMotion,
}: {
  onBrowse: () => void;
  reducedMotion: boolean;
}) {
  return (
    <>
      {/* Top-LEFT: Browse rooms */}
      <div
        className="absolute top-4 left-4 z-20 pointer-events-auto"
        style={{ direction: 'ltr' }}
      >
        <motion.button
          onClick={onBrowse}
          initial="rest"
          whileHover="hover"
          whileTap={{ scale: 0.97 }}
          animate="rest"
          className="nx-corner-chip group relative inline-flex items-center gap-2 px-3 py-2 rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(94,216,255,0.16), rgba(94,216,255,0.05)), rgba(10,16,28,0.55)',
            border: '1px solid rgba(94,216,255,0.45)',
            color: 'rgb(220,245,255)',
            fontFamily: DISPLAY_FONT,
            boxShadow:
              '0 0 18px rgba(94,216,255,0.25), 0 8px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.08)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
          aria-label="Browse open rooms"
        >
          <motion.span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            variants={{
              rest:  { x: '-130%', opacity: 0 },
              hover: { x: '130%',  opacity: reducedMotion ? 0 : 1 },
            }}
            transition={{ type: 'tween', ease: 'easeOut', duration: 0.9 }}
            style={{
              background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)',
            }}
          />
          <div
            className="relative z-10 flex items-center justify-center shrink-0"
            style={{
              width: 26, height: 26, borderRadius: 8,
              background: 'linear-gradient(135deg, rgba(94,216,255,0.30), rgba(94,216,255,0.10))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: 'rgb(220,245,255)',
            }}
          >
            <GlobeIcon />
          </div>
          <span className="relative z-10 text-[12px] font-bold tracking-[0.16em] uppercase">
            Rooms
          </span>
          <motion.span
            aria-hidden
            variants={{ rest: { x: 0, opacity: 0.55 }, hover: { x: 3, opacity: 1 } }}
            transition={{ type: 'spring', stiffness: 380, damping: 22 }}
            className="relative z-10 text-[12px]"
          >
            →
          </motion.span>
        </motion.button>
      </div>

      {/* Top-RIGHT (next to Settings gear): More Games — the operator
          wants this LOUD. A full two-line discovery card with an
          animated conic-gradient ring, breathing icon aura, and a
          sheen sweep. Sits left of the gear (right: 76). */}
      <div
        className="nx-games-snug absolute top-4 z-20 pointer-events-auto"
        style={{ direction: 'ltr', right: 76 }}
      >
        <motion.a
          href="https://blog.cellsnake.com"
          target="_blank"
          rel="noopener noreferrer"
          initial="rest"
          whileHover="hover"
          whileTap={{ scale: 0.96 }}
          animate={reducedMotion ? 'rest' : 'breathe'}
          variants={{
            rest:    { scale: 1 },
            breathe: { scale: [1, 1.03, 1], transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' } },
            hover:   { scale: 1.06, transition: { type: 'spring', stiffness: 320, damping: 20 } },
          }}
          className="group relative inline-flex items-center gap-3 rounded-2xl"
          style={{
            padding: '3px',
            background:
              'linear-gradient(120deg, #d946ef 0%, #5ec8ff 40%, #9a6bff 70%, #d946ef 100%)',
            backgroundSize: '250% 250%',
            animation: reducedMotion ? 'none' : 'nx-games-ring 5s linear infinite',
            boxShadow:
              '0 0 30px rgba(217,70,239,0.50), 0 0 60px rgba(94,216,255,0.28), 0 10px 26px rgba(0,0,0,0.5)',
            fontFamily: DISPLAY_FONT,
          }}
          aria-label="More games on the CellSnake blog (opens in a new tab)"
        >
          {/* Inner body — dark surface so the gradient reads as a ring */}
          <span
            className="relative flex items-center gap-3 rounded-[13px] px-4 py-2.5 overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(16,10,28,0.94), rgba(8,10,20,0.96))',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          >
            {/* Sheen sweep on hover */}
            <motion.span
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              variants={{
                rest:  { x: '-130%', opacity: 0 },
                hover: { x: '130%',  opacity: reducedMotion ? 0 : 1 },
              }}
              transition={{ type: 'tween', ease: 'easeOut', duration: 0.8 }}
              style={{
                background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%)',
              }}
            />
            {/* Icon tile with breathing aura */}
            <span className="relative z-10 flex items-center justify-center shrink-0"
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg, rgba(217,70,239,0.40), rgba(94,216,255,0.30))',
                border: '1px solid rgba(255,255,255,0.22)',
                boxShadow: 'inset 0 0 12px rgba(217,70,239,0.40), 0 0 16px rgba(217,70,239,0.45)',
                color: 'rgb(255,232,252)',
              }}
            >
              <ControllerIcon size={22} />
            </span>
            {/* Two-line label */}
            <span className="relative z-10 flex flex-col leading-tight">
              <span className="nx-games-kicker" style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '0.30em',
                textTransform: 'uppercase', color: 'rgba(240,171,252,0.85)',
              }}>
                Discover
              </span>
              <span style={{
                fontSize: 16, fontWeight: 800, letterSpacing: '0.01em',
                color: '#fff',
                textShadow: '0 0 12px rgba(217,70,239,0.65)',
                whiteSpace: 'nowrap',
              }}>
                More Games <span aria-hidden style={{ fontSize: 13, opacity: 0.9 }}>↗</span>
              </span>
            </span>
          </span>
        </motion.a>
      </div>

      {/* Keyframes for the rotating gradient ring on the More Games
          card. Local <style> so the animation ships with the component. */}
      <style>{`
        @keyframes nx-games-ring {
          0%   { background-position: 0% 50%; }
          100% { background-position: 250% 50%; }
        }
        @media (max-width: 560px) {
          /* Phones: the two-line More-Games card overlaps the ROOMS
             chip at 390px wide. Scale it down from its top-right
             corner and drop the DISCOVER kicker so both chips
             coexist. */
          .nx-games-snug { transform: scale(0.76); transform-origin: top right; }
          .nx-games-kicker { display: none; }
          /* The scroll chevron sits behind the taller wrapped footer
             nav on phones — hide it; thumbs scroll naturally. */
          .nx-scroll-cue { display: none !important; }
        }
      `}</style>
    </>
  );
}

/**
 * BelowFold — the scrollable content section under the hero. Real
 * English copy about the game (~500 words), feature highlights,
 * guide links and a mini-FAQ. Exists for two audiences at once:
 * players deciding whether to press Play, and Google's AdSense /
 * search crawlers that need substantial text on the root URL.
 *
 * Styling matches site.css (the static content pages) so a visitor
 * moving between / and /guides/ sees one coherent brand.
 */
/** Scroll-reveal wrapper — children fade+rise into view the first
 *  time they enter the viewport. Framer's whileInView handles the
 *  IntersectionObserver plumbing; `once` keeps it from re-triggering
 *  on scroll-up. Respects prefers-reduced-motion via framer's global
 *  handling (transform-only animation degrades gracefully). */
function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay, ease: [0.2, 0.7, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function BelowFold() {
  const h2Style: React.CSSProperties = {
    fontFamily: DISPLAY_FONT,
    fontSize: 24,
    fontWeight: 700,
    color: '#e8f2ff',
    margin: '0 0 14px',
    letterSpacing: '-0.01em',
  };
  const pStyle: React.CSSProperties = {
    color: 'rgba(230,240,255,0.82)',
    fontSize: 15.5,
    lineHeight: 1.75,
    margin: '0 0 14px',
  };
  const cardStyle: React.CSSProperties = {
    background: 'rgba(17,24,39,0.62)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '20px 22px',
    textDecoration: 'none',
    display: 'block',
    transition: 'border-color .2s, background .2s',
  };
  return (
    <section
      id="about-the-game"
      style={{
        background:
          'radial-gradient(ellipse 60% 30% at 50% 0%, rgba(94,216,255,0.05), transparent 60%), #04050b',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '64px 24px 72px' }}>

        {/* Intro */}
        <Reveal>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            fontFamily: DISPLAY_FONT, fontSize: 11, letterSpacing: '0.34em',
            textTransform: 'uppercase', color: '#5ec8ff', fontWeight: 700, marginBottom: 10,
          }}>
            About the game
          </div>
          <h2 style={{ ...h2Style, fontSize: 'clamp(26px, 4vw, 36px)' }}>
            A free multiplayer snake arena,<br />built for the modern web
          </h2>
        </div>
        </Reveal>

        <Reveal delay={0.05}>
        <p style={pStyle}>
          <strong style={{ color: '#5ec8ff' }}>CellSnake</strong> is a free
          multiplayer game in the snake.io tradition: you steer a neon snake
          through an open arena shared with real players and AI rivals, eating
          glowing pellets to grow and avoiding everyone else's body. If your
          head touches another snake, you burst into pellets — and someone
          else gets bigger. There's nothing to install and no account to
          create: open the page, press Play, and you're in a live room within
          a second, on desktop or mobile.
        </p>
        <p style={pStyle}>
          What makes CellSnake different is that your <em>color matters</em>.
          Pellets matching your chosen color are worth 2.5× the mass, which
          turns every run into a series of real decisions — chase the matching
          trail, contest a fallen rival's remains, or play it safe and farm
          the mid-ring. New players get real protection too: a spawn shield,
          guaranteed starter pellets in their color, and a full minute during
          which AI rivals deliberately keep their distance.
        </p>
        </Reveal>

        {/* Feature cards */}
        <Reveal delay={0.05}>
        <div style={{
          display: 'grid', gap: 14, margin: '36px 0 48px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        }}>
          {[
            { icon: '⚡', title: 'Instant play', text: 'One click from URL to live arena. No download, no sign-up, no tutorial gate.' },
            { icon: '🎨', title: 'Color strategy', text: 'Matching pellets are worth 2.5× — your color choice is your build.' },
            { icon: '🛡️', title: 'Fair starts', text: 'Spawn shields, starter pellets, and 60 seconds of newcomer protection.' },
            { icon: '📱', title: 'True mobile play', text: 'A floating joystick with integrated boost — and a wider pickup radius for touch.' },
          ].map((f) => (
            <div key={f.title} style={cardStyle}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>{f.icon}</div>
              <div style={{ fontFamily: DISPLAY_FONT, fontSize: 15, fontWeight: 700, color: '#eef4ff', marginBottom: 6 }}>
                {f.title}
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>{f.text}</div>
            </div>
          ))}
        </div>

        </Reveal>

        {/* Guides */}
        <Reveal>
        <h2 style={h2Style}>Learn the game</h2>
        <p style={pStyle}>
          We publish serious strategy content, written from the data of
          21,000+ real play sessions:
        </p>
        <div style={{
          display: 'grid', gap: 16, margin: '20px 0 48px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}>
          {[
            { href: '/how-to-play.html', icon: '📖', title: 'How to Play', text: 'Controls and core mechanics in five minutes.' },
            { href: '/guides/', icon: '📚', title: 'Guide Library', text: 'Early game, mid game, late game, PvP, mobile.' },
            { href: '/tips.html', icon: '🎯', title: 'Tips & Strategy', text: 'The seven rules that build leaderboard runs.' },
            { href: '/faq.html', icon: '❓', title: 'FAQ', text: 'Eighteen common questions, answered straight.' },
          ].map((g) => (
            <a key={g.href} href={g.href} style={cardStyle}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(94,216,255,0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              <div style={{ fontSize: 26, marginBottom: 10 }}>{g.icon}</div>
              <div style={{ fontFamily: DISPLAY_FONT, fontSize: 15, fontWeight: 700, color: '#5ec8ff', marginBottom: 6 }}>
                {g.title}
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>{g.text}</div>
            </a>
          ))}
        </div>

        </Reveal>

        {/* Mini FAQ */}
        <Reveal>
        <h2 style={h2Style}>Quick answers</h2>
        {[
          { q: 'Is it really free?', a: 'Completely. No purchases, no premium currency — the game is supported by unobtrusive ads.' },
          { q: 'Are the other players real?', a: 'Rooms mix real players with AI rivals so there’s always action. Every human who joins replaces one AI.' },
          { q: 'Does it work on my phone?', a: 'Fully — a floating joystick with built-in boost, plus a wider pellet-pickup radius to offset touch precision.' },
          { q: 'Where is my progress saved?', a: 'Best score and settings live in your browser. No accounts, nothing to lose but the run you’re on.' },
        ].map((f) => (
          <div key={f.q} style={{ ...cardStyle, marginBottom: 12 }}>
            <div style={{ fontFamily: DISPLAY_FONT, fontSize: 14.5, fontWeight: 700, color: '#eef4ff', marginBottom: 6 }}>
              <span style={{ color: '#5ec8ff', fontWeight: 800 }}>Q&nbsp;</span>{f.q}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.62)', lineHeight: 1.65 }}>{f.a}</div>
          </div>
        ))}
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <a href="/faq.html" style={{ color: '#5ec8ff', fontSize: 13.5, textDecoration: 'underline', textUnderlineOffset: 3 }}>
            All 18 questions →
          </a>
        </div>

        </Reveal>

        {/* Bottom CTA */}
        <Reveal>
        <div style={{
          marginTop: 56, textAlign: 'center', padding: '36px 24px',
          background: 'linear-gradient(180deg, rgba(94,216,255,0.07), rgba(154,107,255,0.05))',
          border: '1px solid rgba(94,216,255,0.28)', borderRadius: 18,
        }}>
          <div style={{ fontFamily: DISPLAY_FONT, fontSize: 22, fontWeight: 700, color: '#eef4ff', marginBottom: 18 }}>
            The arena is live right now.
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(180deg, #57e3ff 0%, #4b9bff 45%, #9a6bff 100%)',
              color: '#fff', border: 'none', cursor: 'pointer',
              fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 15,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '14px 34px', borderRadius: 14,
              boxShadow: '0 8px 28px rgba(94,216,255,0.40)',
            }}
          >
            ▶ Back to top — Play
          </button>
        </div>
        </Reveal>

        {/* Footer links */}
        <div style={{
          marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', flexWrap: 'wrap', gap: '8px 22px', justifyContent: 'center',
        }}>
          {[
            ['/how-to-play.html', 'How to Play'], ['/guides/', 'Guides'],
            ['/tips.html', 'Tips'], ['/faq.html', 'FAQ'], ['/updates.html', 'Updates'],
            ['/about.html', 'About'], ['/contact.html', 'Contact'],
            ['/privacy.html', 'Privacy'], ['/terms.html', 'Terms'],
          ].map(([href, label]) => (
            <a key={href} href={href} style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, textDecoration: 'none' }}>
              {label}
            </a>
          ))}
          <a href="https://blog.cellsnake.com" target="_blank" rel="noopener noreferrer"
             style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, textDecoration: 'none' }}>
            GameNest Blog ↗
          </a>
        </div>
        <div style={{ textAlign: 'center', fontSize: 11.5, color: 'rgba(255,255,255,0.35)', marginTop: 18 }}>
          © 2026 CellSnake · Free browser game · No installs, no accounts
        </div>
      </div>
    </section>
  );
}

/**
 * ContentFooter — small text-page nav below the hero card. Links to
 * How-to-Play / Tips / About / FAQ / Updates / Blog.
 *
 * Beyond the operator's benefit (players can browse), this is
 * critical for AdSense: the reviewer's first rejection was
 * "low-value content", which fires when a site is a canvas-only
 * game with no readable text. These text pages (each 400-800 words
 * of real, useful content) satisfy that gate — and the footer
 * makes them one click away from the root URL so the review
 * crawler finds them without effort.
 *
 * Positioned at the BOTTOM of the viewport (absolute, bottom-2)
 * with a soft background gradient so it stays discoverable but
 * doesn't compete with the primary Play CTA.
 */
function ContentFooter() {
  const items = [
    { href: '/how-to-play.html', label: 'How to Play', icon: '📖' },
    { href: '/guides/',          label: 'Guides',      icon: '📚' },
    { href: '/tips.html',        label: 'Tips',        icon: '🎯' },
    { href: '/faq.html',         label: 'FAQ',         icon: '❓' },
    { href: '/updates.html',     label: 'Updates',     icon: '🆕' },
    { href: '/about.html',       label: 'About',       icon: 'ℹ' },
    { href: '/privacy.html',     label: 'Privacy',     icon: '🔒' },
    { href: '/terms.html',       label: 'Terms',       icon: '📜' },
    { href: '/contact.html',     label: 'Contact',     icon: '✉️' },
    { href: 'https://blog.cellsnake.com', label: 'More Games', icon: '↗', external: true },
  ];
  // Mobile: the rail collapses behind a ☰ toggle (a 10-item column
  // would cover the PLAY card on a 390px screen). Desktop: always
  // expanded. The CSS media queries in the <style> below decide
  // which of the two presentations is visible.
  const [open, setOpen] = useState(false);
  return (
    <>
      <style>{`
        .nx-rail { display: flex; }
        .nx-rail-toggle { display: none; }
        @media (max-width: 900px) {
          .nx-rail { display: ${open ? 'flex' : 'none'}; }
          .nx-rail-toggle { display: inline-flex; }
        }
      `}</style>

      {/* ── LEFT rail — moved from the bottom strip so Google's
             anchor ads (which dock to the bottom edge) never overlap
             the site navigation. Bottom-LEFT anchored, stacked
             upward, clear of the ROOMS chip at top-left. */}
      <div
        className="absolute z-10 pointer-events-none"
        style={{
          left: 'max(12px, env(safe-area-inset-left, 0px))',
          bottom: 'max(14px, env(safe-area-inset-bottom, 0px))',
          direction: 'ltr',
        }}
      >
        {/* Mobile toggle — one small chip; tap to reveal the rail. */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="nx-rail-toggle pointer-events-auto items-center gap-2 px-3 py-2 rounded-xl mb-2"
          style={{
            background: 'linear-gradient(180deg, rgba(17,24,39,0.72), rgba(5,7,13,0.82))',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'rgba(220,240,255,0.85)',
            fontFamily: DISPLAY_FONT,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            cursor: 'pointer',
          }}
          aria-expanded={open}
          aria-label="Toggle site navigation"
        >
          <span aria-hidden>{open ? '✕' : '☰'}</span>
          <span>{open ? 'Close' : 'Info'}</span>
        </button>

        {/* The rail itself — vertical column of nav pills. */}
        <div
          className="nx-rail flex-col gap-1 px-2 py-2 rounded-2xl pointer-events-auto"
          style={{
            background: 'linear-gradient(180deg, rgba(17,24,39,0.55), rgba(5,7,13,0.68))',
            border: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          {items.map((it) => (
            <a
              key={it.href}
              href={it.href}
              {...(it.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-lg text-[11px] font-semibold uppercase tracking-[0.12em] transition-all"
              style={{
                color: 'rgba(220,240,255,0.72)',
                fontFamily: DISPLAY_FONT,
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(94,216,255,0.10)'; (e.currentTarget as HTMLAnchorElement).style.color = 'rgb(220,240,255)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(220,240,255,0.72)'; }}
            >
              <span aria-hidden style={{ fontSize: 12, width: 16, textAlign: 'center' }}>{it.icon}</span>
              <span>{it.label}</span>
            </a>
          ))}
        </div>
      </div>
    </>
  );
}

function GlobeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

/** Game controller / arcade glyph for the "More games" CTA — the
 *  D-pad + buttons silhouette reads instantly as "games" without
 *  needing text reinforcement. Inherits color via currentColor so
 *  the button's text-shadow glow carries through. */
function ControllerIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden
      style={{ filter: 'drop-shadow(0 0 4px rgba(245,225,255,0.6))' }}
    >
      <rect x="2.5" y="7.5" width="19" height="11" rx="5.5" stroke="currentColor" strokeWidth="1.6" />
      {/* D-pad */}
      <path d="M6.5 11.5v3M5 13h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      {/* Action buttons */}
      <circle cx="16" cy="11.5" r="1.1" fill="currentColor" />
      <circle cx="18" cy="13.5" r="1.1" fill="currentColor" />
      <circle cx="14" cy="13.5" r="1.1" fill="currentColor" />
      <circle cx="16" cy="15.5" r="1.1" fill="currentColor" />
    </svg>
  );
}

/**

/**
 * Live social proof — fetches /api/public-stats and renders a tiny
 * dual chip: "X playing now · X today". Hidden until the fetch
 * resolves so we don't flash "0 today" briefly on cold load.
 *
 * Heaviest dopamine for a returning visitor: "527 played today"
 * frames the game as "alive and busy" without a screenshot or video.
 */
function SocialProof() {
  const [stats, setStats] = useState<{ liveNow: number; playersToday: number; playersAllTime: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/public-stats', { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (!cancelled && j && typeof j.playersToday === 'number') {
          setStats({
            liveNow: j.liveNow ?? 0,
            playersToday: j.playersToday ?? 0,
            playersAllTime: j.playersAllTime ?? 0,
          });
        }
      })
      .catch(() => { /* silent — chip just won't render */ });
    return () => { cancelled = true; };
  }, []);

  if (!stats) return null;
  const { liveNow, playersToday, playersAllTime } = stats;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center mb-5 inline-flex items-center justify-center w-full"
    >
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-semibold">
        {liveNow > 0 && (
          <>
            <span className="relative inline-flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              <span className="text-emerald-300 tabular-nums font-bold">{liveNow}</span>
              <span className="text-white/55">playing now</span>
            </span>
            <span className="text-white/15">·</span>
          </>
        )}
        {playersToday > 0 ? (
          <span className="text-white/55">
            <span className="text-cyan-200 tabular-nums font-bold">
              {playersToday.toLocaleString()}
            </span>{' '}
            today
          </span>
        ) : null}
        {playersAllTime > 100 && (
          <>
            <span className="text-white/15">·</span>
            <span className="text-white/45">
              <span className="text-white/75 tabular-nums font-bold">
                {playersAllTime > 9999 ? Math.round(playersAllTime / 1000) + 'k' : playersAllTime.toLocaleString()}
              </span>{' '}
              all-time
            </span>
          </>
        )}
      </div>
    </motion.div>
  );
}

/**
 * DailyChallengeChip — compact today's-goal banner on the landing
 * card. Surfaces the user's unfinished daily challenge progress so a
 * returning player sees momentum: "2/5 done — keep going." Hidden
 * when the challenge for today is already completed (no point
 * begging the player to do something already finished).
 *
 * Per the retention analysis, daily challenges drove an estimated
 * 18 % D1 retention lift in comparable games. The chip is small
 * (one row + thin progress bar) so it doesn't compete with the
 * primary Play CTA above.
 */
function DailyChallengeChip(): React.ReactElement | null {
  const challenge = useGameStore((s) => s.dailyChallenge);
  const progress = useGameStore((s) => s.dailyProgress);
  const refresh = useGameStore((s) => s.refreshDailyChallenge);
  useEffect(() => { refresh(); }, [refresh]);

  if (progress.completed) return null;

  const pct = Math.max(0, Math.min(1, progress.current / challenge.goal));
  const color = `hsl(${challenge.hue} 100% 65%)`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25, duration: 0.4 }}
      className="mb-3 mx-auto px-3 py-2 rounded-xl"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: `0 0 16px ${color}22, inset 0 0 14px rgba(255,255,255,0.02)`,
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: color, boxShadow: `0 0 6px ${color}` }}
          />
          <span className="text-[10px] uppercase tracking-[0.24em] text-white/80 font-semibold truncate">
            Daily Challenge
          </span>
        </div>
        <span className="text-[10px] tabular-nums font-mono" style={{ color }}>
          {Math.round(progress.current)} / {challenge.goal}
        </span>
      </div>
      <div className="text-[11px] text-white/65 leading-snug mb-2">
        {challenge.label}
      </div>
      <div
        className="h-1 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct * 100}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{
            background: `linear-gradient(90deg, ${color} 0%, ${color}aa 100%)`,
            boxShadow: `0 0 8px ${color}88`,
          }}
        />
      </div>
    </motion.div>
  );
}
